// https://fluentsite.z22.web.core.windows.net/quick-start
import React, { useState, useEffect } from "react";
import {
  FluentProvider,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import { HashRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { useTeamsUserCredential } from "@microsoft/teamsfx-react";
import { app } from "@microsoft/teams-js";
import Privacy from "./Privacy";
import TermsOfUse from "./TermsOfUse";
import Tab from "./Tab";
import ErrorPage from "./ErrorPage";
import { TeamsFxContext } from "./Context";
import config from "./sample/lib/config";
import { AuthenticationService } from "../Services/AuthenticationService";

/**
 * The main app which handles the initialization and routing
 * of the app.
 */
export default function App() {
  const { loading, theme, themeString, teamsUserCredential } = useTeamsUserCredential({
    initiateLoginEndpoint: config.initiateLoginEndpoint!,
    clientId: config.clientId!,
  });

  document.documentElement.setAttribute("data-theme", themeString === "dark" ? "dark" : "light");

  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Authenticatie bij Teams...");

  // Wacht tot useTeamsUserCredential klaar is (initialiseert Teams SDK intern)
  // Dan pas AuthenticationService.init() aanroepen
  useEffect(() => {
    const initAuth = async () => {
      if (!loading) {
        try {
          setLoadingMessage("Authenticatie bij DOSP...");
          await AuthenticationService.init();
          // Signaleer Teams dat de app volledig geladen is
          app.notifySuccess();
        } catch (error) {
          console.error("Authentication initialization failed", error);
          setAuthError(true);

          const errorDetails = {
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent,
            context: 'Authentication'
          };
          sessionStorage.setItem('errorDetails', JSON.stringify(errorDetails));
        } finally {
          setAuthLoading(false);
        }
      }
    };
    initAuth();
  }, [loading]);

  const isLoading = loading || authLoading;

  return (
    <TeamsFxContext.Provider value={{ theme, themeString, teamsUserCredential }}>
      <FluentProvider
        theme={
          themeString === "dark"
            ? teamsDarkTheme
            : themeString === "contrast"
            ? teamsHighContrastTheme
            : {
                ...teamsLightTheme,
                colorNeutralBackground3: "#eeeeee",
              }
        }
        style={{ background: tokens.colorNeutralBackground3 }}
      >
        <Router>
          {isLoading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              width: '100%',
              gap: '16px'
            }}>
              <Spinner
                size="extra-large"
                label={loadingMessage}
                labelPosition="below"
              />
            </div>
          ) : authError ? (
            <ErrorPage />
          ) : (
            <Routes>
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/termsofuse" element={<TermsOfUse />} />
              <Route path="/tab" element={<Tab />} />
              <Route path="/error" element={<ErrorPage />} />
              <Route path="*" element={<Navigate to={"/tab"} />}></Route>
            </Routes>
          )}
        </Router>
      </FluentProvider>
    </TeamsFxContext.Provider>
  );
}
